/**
 * cookieConsent — tiny persistence + pub/sub layer for the cookie
 * consent banner (`components/CookieConsent.tsx`).
 *
 * Design:
 *   - The visitor's choice ('accepted' | 'rejected') is persisted to
 *     `localStorage` **with a timestamp** so the banner doesn't reappear
 *     on every page load once a decision has been made — but the choice
 *     naturally expires after {@link CONSENT_TTL_MS} (one week) and the
 *     banner is shown again, regardless of whether the visitor previously
 *     accepted or rejected.
 *   - `resetCookieConsent()` clears the stored choice and broadcasts a
 *     change so the banner can reopen without a full page reload — used
 *     by the "Cookie Settings" link in the footer so visitors can revisit
 *     their choice at any time (a GDPR withdrawal-of-consent requirement).
 *   - Consumers that need to gate third-party scripts (analytics,
 *     marketing pixels, etc.) behind consent should call
 *     `getCookieConsent()` before loading them, and/or subscribe via
 *     `subscribeCookieConsent()` to react live if the visitor changes
 *     their mind after scripts were already loaded.
 */

export type CookieConsentValue = 'accepted' | 'rejected';

interface StoredConsent {
  value: CookieConsentValue;
  /** `Date.now()` at the time the choice was made. */
  timestamp: number;
}

const STORAGE_KEY = 'otpl-cookie-consent';
const EVENT_NAME = 'otpl-cookie-consent-changed';

/** How long a stored choice (accept OR reject) stays valid before the
 *  banner is shown again — one week. */
const CONSENT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function readStored(): StoredConsent | null {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredConsent>;
    if (
      (parsed.value === 'accepted' || parsed.value === 'rejected') &&
      typeof parsed.timestamp === 'number'
    ) {
      return { value: parsed.value, timestamp: parsed.timestamp };
    }
  } catch {
    // Ignore malformed/legacy (pre-TTL) values — treated as undecided below.
  }
  return null;
}

/**
 * Read the visitor's stored consent choice, or `null` if undecided /
 * expired (older than {@link CONSENT_TTL_MS}). An expired entry is
 * removed from storage so the banner re-prompts on next check.
 */
export function getCookieConsent(): CookieConsentValue | null {
  if (typeof window === 'undefined') return null;
  const stored = readStored();
  if (!stored) return null;
  if (Date.now() - stored.timestamp > CONSENT_TTL_MS) {
    window.localStorage.removeItem(STORAGE_KEY);
    return null;
  }
  return stored.value;
}

/** Persist the visitor's choice with the current timestamp and notify listeners. */
export function setCookieConsent(value: CookieConsentValue): void {
  const stored: StoredConsent = { value, timestamp: Date.now() };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  window.dispatchEvent(new CustomEvent<CookieConsentValue | null>(EVENT_NAME, { detail: value }));
}

/** Clear the stored choice (re-opens the banner) and notify listeners. */
export function resetCookieConsent(): void {
  window.localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent<CookieConsentValue | null>(EVENT_NAME, { detail: null }));
}

/**
 * Subscribe to consent changes (accept / reject / reset). Returns an
 * unsubscribe function. Used by the banner itself to stay in sync with
 * the "Cookie Settings" reopen trigger living elsewhere in the tree.
 */
export function subscribeCookieConsent(
  callback: (value: CookieConsentValue | null) => void,
): () => void {
  const handler = (event: Event): void => {
    callback((event as CustomEvent<CookieConsentValue | null>).detail ?? null);
  };
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}
